/**
 * Group and group-invite persistence schema.
 *
 * Invariants:
 * - Group membership is unique per `(groupId, userId)`.
 * - Invite tokens are globally unique; usage/expiry is enforced in domain logic.
 *
 * @see `packages/core/docs/database.md`
 */
import { pgTable, uuid, text, primaryKey, integer, index, uniqueIndex } from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";
import { user } from "../core/user.sql";
import { space } from "../space/space.sql";
import { id, temporalInstant } from "../../utils/sql";

export const group = pgTable("group", {
  ...id,
  timeCreated: temporalInstant("time_created").notNull(),
  timeUpdated: temporalInstant("time_updated").notNull(),
});

export const groupRelations = relations(group, ({ many, one }) => ({
  members: many(groupMember),
  invites: many(groupInvite),
  space: one(space, {
    fields: [group.id],
    references: [space.id],
  }),
}));

export const groupMember = pgTable(
  "group_member",
  {
    groupId: uuid("group_id")
      .references(() => group.id)
      .notNull(),
    userId: uuid("user_id")
      .references(() => user.id)
      .notNull(),
  },
  (t) => [primaryKey({ name: "group_member_pk", columns: [t.groupId, t.userId] })],
);

export const groupMemberRelations = relations(groupMember, ({ one }) => ({
  group: one(group, {
    fields: [groupMember.groupId],
    references: [group.id],
  }),
}));

export const groupInvite = pgTable(
  "group_invite",
  {
    ...id,
    groupId: uuid("group_id")
      .references(() => group.id)
      .notNull(),
    token: text("token").notNull(),
    createdById: uuid("created_by_id").references(() => user.id),
    maxUses: integer("max_uses"),
    useCount: integer("use_count").notNull(),
    timeExpires: temporalInstant("time_expires"),
    timeCreated: temporalInstant("time_created").notNull(),
  },
  (t) => [
    uniqueIndex("group_invite_token_idx").on(t.token),
    index("group_invite_group_id_idx").on(t.groupId),
  ],
);

export const groupInviteRelations = relations(groupInvite, ({ one }) => ({
  group: one(group, {
    fields: [groupInvite.groupId],
    references: [group.id],
  }),
}));
